import { supabase } from '@/lib/supabase';

export type AdMediaType = 'image' | 'video';

export interface SponsoredAd {
  id: string;
  owner_id: string | null;
  sponsor: string;
  title: string;
  description: string | null;
  media_type: AdMediaType;
  media_url: string;
  link_url: string | null;
  cta_label: string | null;
  category: string | null;
  status: string;
  target_pages: string[];
  created_at: string;
}

export interface PublishAdInput {
  sponsor: string;
  title: string;
  description: string;
  category: string;
  mediaType: AdMediaType;
  mediaUrl: string;
  linkUrl: string;
  ctaLabel: string;
  targetPages: string[];
}

/** Clé spéciale : afficher la pub sur toutes les pages. */
export const ALL_PAGES_KEY = 'all';

/** Liste des pages ciblables pour une publicité. */
export interface AdPageTarget {
  key: string;
  label: string;
}

export const AD_PAGE_TARGETS: AdPageTarget[] = [
  { key: 'home', label: 'Accueil' },
  { key: 'media', label: 'Média' },
  { key: 'directory', label: 'Annuaire' },
  { key: 'shop', label: 'Boutique' },
  { key: 'work', label: 'Freelance' },
  { key: 'jobs', label: 'Emplois' },
  { key: 'learn', label: 'Cours' },
  { key: 'forum', label: 'Forum' },
  { key: 'apartments', label: 'Immobilier' },
  { key: 'listings', label: 'Annonces' },
  { key: 'blog', label: 'Blog' },
  { key: 'culture', label: 'Culture' },
  { key: 'ai', label: 'IA' },
  { key: 'cloud', label: 'Cloud' },
  { key: 'pay', label: 'Paiement' },
];

const ADS_BUCKET = 'media';

function parseTargetPages(raw: unknown): string[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter((p): p is string => typeof p === 'string');
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.filter((p): p is string => typeof p === 'string');
      }
    } catch {
      return [];
    }
  }
  return [];
}

export async function fetchSponsoredAds(): Promise<SponsoredAd[]> {
  const { data, error } = await supabase
    .from('sponsored_ads')
    .select('*')
    .eq('status', 'active')
    .order('created_at', { ascending: false });
  if (error) throw error;
  const rows = (data as Array<Omit<SponsoredAd, 'target_pages'> & { target_pages?: unknown }>) || [];
  return rows.map((row) => ({
    ...row,
    target_pages: parseTargetPages(row.target_pages),
  }));
}

/**
 * Filtre les publicités pour une page donnée.
 * - `all` dans target_pages → affichée partout.
 * - aucune page définie → affichée uniquement sur la page Média (comportement par défaut).
 * - liste précise → affichée uniquement sur ces pages.
 */
export function adsForPage(ads: SponsoredAd[], pageKey: string): SponsoredAd[] {
  return ads.filter((ad) => {
    const pages = ad.target_pages || [];
    if (pages.includes(ALL_PAGES_KEY)) return true;
    if (pages.length === 0) return pageKey === 'media';
    return pages.includes(pageKey);
  });
}

export async function uploadAdImage(file: File): Promise<string> {
  const ext = file.name.split('.').pop()?.toLowerCase() || 'bin';
  const safeName = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}.${ext}`;
  const path = `ads/${safeName}`;

  const { error } = await supabase.storage.from(ADS_BUCKET).upload(path, file, {
    cacheControl: '3600',
    upsert: false,
  });
  if (error) throw error;

  const { data } = supabase.storage.from(ADS_BUCKET).getPublicUrl(path);
  return data.publicUrl;
}

export async function publishSponsoredAd(input: PublishAdInput): Promise<SponsoredAd> {
  const { data: sessionData } = await supabase.auth.getSession();
  const user = sessionData?.session?.user;
  if (!user) throw new Error('Vous devez être connecté pour publier une publicité.');

  const targetPages = input.targetPages || [];

  const { data, error } = await supabase
    .from('sponsored_ads')
    .insert({
      owner_id: user.id,
      sponsor: input.sponsor,
      title: input.title,
      description: input.description || null,
      category: input.category || null,
      media_type: input.mediaType,
      media_url: input.mediaUrl,
      link_url: input.linkUrl || null,
      cta_label: input.ctaLabel || null,
      target_pages: targetPages.length > 0 ? JSON.stringify(targetPages) : null,
    })
    .select()
    .single();
  if (error) throw error;
  return { ...(data as SponsoredAd), target_pages: targetPages };
}

export async function deleteSponsoredAd(id: string): Promise<void> {
  const { error } = await supabase.from('sponsored_ads').delete().eq('id', id);
  if (error) throw error;
}